import type {
  Candidate,
  ClientToTracker,
  GeoHint,
  MetricsReport,
  PeerId,
  PeersMsg,
  RtcSignalData,
  TrackerToClient,
} from "@p2p-hls/protocol";
import { Emitter } from "./emitter.js";
import type { EngineConfig } from "./types.js";

type SignalingEvents = {
  peers: PeersMsg;
  signal: { from: PeerId; data: RtcSignalData; fromGeo: GeoHint };
  "peer-gone": PeerId;
  open: void;
  close: void;
};

/**
 * WebSocket client to the tracker. It only speaks the signaling protocol; it
 * never carries media. `join()` is deliberately NOT called on construction —
 * the engine calls it on the player's `play` event so P2P activates on play.
 */
/** Reconnect backoff bounds (ms). */
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 15000;

export class SignalingClient extends Emitter<SignalingEvents> {
  private ws: WebSocket | null = null;
  private joined = false;
  private stopped = false;
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly config: EngineConfig,
    private readonly peerId: PeerId,
  ) {
    super();
  }

  connect(): void {
    this.stopped = false;
    const url = new URL(this.config.trackerUrl);
    url.searchParams.set("peerId", this.peerId);
    // Surface streamId in the connect URL so an edge LB can hash-route this WS to
    // the tracker shard that owns the stream (the tracker itself reads streamId
    // from the join message and ignores this param — safe for single-node too).
    url.searchParams.set("stream", this.config.streamId);
    const sim = this.config.geoSim;
    if (sim?.asn != null) url.searchParams.set("asn", String(sim.asn));
    if (sim?.country) url.searchParams.set("country", sim.country);
    if (sim?.region) url.searchParams.set("region", sim.region);

    const ws = new WebSocket(url.toString());
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.reconnectDelay = RECONNECT_MIN_MS; // reset backoff on success
      this.emit("open", undefined);
      if (this.joined) this.sendJoin();
    });
    ws.addEventListener("close", () => {
      this.emit("close", undefined);
      // Keep the swarm alive across transient tracker/network drops.
      if (!this.stopped && this.joined) this.scheduleReconnect();
    });
    ws.addEventListener("error", () => ws.close());
    ws.addEventListener("message", (ev) => {
      const msg = JSON.parse(ev.data as string) as TrackerToClient;
      switch (msg.t) {
        case "peers":
          this.emit("peers", msg);
          break;
        case "signal":
          this.emit("signal", { from: msg.from, data: msg.data, fromGeo: msg.fromGeo });
          break;
        case "peer-gone":
          this.emit("peer-gone", msg.peerId);
          break;
      }
    });
  }

  /** Announce we are watching — joins the swarm. Idempotent. */
  join(): void {
    this.joined = true;
    if (this.ws?.readyState === WebSocket.OPEN) this.sendJoin();
    else if (!this.ws) this.connect();
  }

  leave(): void {
    this.joined = false;
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.send({ t: "leave", peerId: this.peerId });
    this.ws?.close();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(RECONNECT_MAX_MS, this.reconnectDelay * 2);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.connect(); // `joined` stays true → rejoins on open
    }, delay);
  }

  relaySignal(to: PeerId, data: RtcSignalData): void {
    this.send({ t: "signal", from: this.peerId, to, data });
  }

  /** Report a compact metrics snapshot to the tracker for the swarm dashboard. */
  sendMetrics(report: MetricsReport): void {
    this.send({ t: "metrics", peerId: this.peerId, streamId: this.config.streamId, report });
  }

  private sendJoin(): void {
    this.send({ t: "join", streamId: this.config.streamId, peerId: this.peerId });
  }

  private send(msg: ClientToTracker): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }
}

export type { Candidate };
